import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, Teleport as _Teleport, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["innerHTML"]

import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

type Position = 'top' | 'bottom' | 'left' | 'right'

interface Props {
    content?: string
    position?: Position
    offset?: number
    theme?: 'green' | 'dark'
}

const ARROW_OFFSET = 6

// we want to have a gap between the tooltip and the window edge
const WINDOW_EDGE_PADDING = 10


export default /*@__PURE__*/_defineComponent({
  __name: 'Tooltip',
  props: {
    content: {},
    position: { default: 'top' },
    offset: { default: 22 },
    theme: { default: 'green' }
  },
  setup(__props: any) {

const props = __props

const trigger = ref<HTMLElement | null>(null)
const tooltip = ref<HTMLElement | null>(null)
const isVisible = ref(false)
const showTimeout = ref<number | null>(null)

const adjustedPosition = ref<Position>(props.position)

const updatePosition = () => {
    const triggerElement = trigger.value?.firstElementChild
    if (!tooltip.value || !triggerElement) {
        return
    }

    const tooltipRect = tooltip.value.getBoundingClientRect()
    const triggerRect = triggerElement.getBoundingClientRect()

    // Get window dimensions and scroll position
    const windowHeight = window.innerHeight
    const windowWidth = window.innerWidth

    // Reset to original position
    adjustedPosition.value = props.position
    const space = {
        top: triggerRect.top, // Space from top of viewport
        bottom: windowHeight - triggerRect.bottom, // Space from bottom of viewport
        left: triggerRect.left, // Space from left of viewport
        right: windowWidth - triggerRect.right, // Space from right of viewport
    }

    const offset = props.offset + ARROW_OFFSET + WINDOW_EDGE_PADDING

    // Check if there's enough space in the preferred position
    switch (props.position) {
        case 'top':
            if (space.top < tooltipRect.height + offset) {
                adjustedPosition.value = 'bottom'
            }
            break
        case 'bottom':
            if (space.bottom < tooltipRect.height + offset) {
                adjustedPosition.value = 'top'
            }
            break
        case 'left':
            if (space.left < tooltipRect.width + offset) {
                adjustedPosition.value = 'right'
            }
            break
        case 'right':
            if (space.right < tooltipRect.width + offset) {
                adjustedPosition.value = 'left'
            }
            break
        default:
            break
    }
}

const hideTooltip = () => {
    if (showTimeout.value) {
        clearTimeout(showTimeout.value)
        showTimeout.value = null
    }
    isVisible.value = false
}

const showTooltip = () => {
    if (showTimeout.value) {
        clearTimeout(showTimeout.value)
    }
    // we want to have a delay before the tooltip is shown
    showTimeout.value = window.setTimeout(() => {
        isVisible.value = true
    }, 100)
}

const tooltipStyle = ref({})

const calculateTooltipStyle = () => {
    const triggerElement = trigger.value?.firstElementChild
    if (!triggerElement) {
        return
    }

    const rect = triggerElement?.getBoundingClientRect()

    let top = 0
    let left = 0
    let transform = ''

    const offset = props.offset + ARROW_OFFSET

    switch (adjustedPosition.value) {
        case 'top':
            top = rect.top - offset
            left = rect.left + rect.width / 2
            transform = 'translate(-50%, -100%)'
            break
        case 'bottom':
            top = rect.bottom + offset
            left = rect.left + rect.width / 2
            transform = 'translate(-50%, 0)'
            break
        case 'left':
            top = rect.top + rect.height / 2
            left = rect.left - offset
            transform = 'translate(-100%, -50%)'
            break
        case 'right':
            top = rect.top + rect.height / 2
            left = rect.right + offset
            transform = 'translate(0, -50%)'
            break
        default:
            break
    }

    tooltipStyle.value = {
        position: 'fixed' as const,
        top: `${top}px`,
        left: `${left}px`,
        transform,
    }
}

const handleScroll = () => {
    hideTooltip()
}

// Update position when tooltip becomes visible
watch(isVisible, (visible) => {
    if (visible) {
        // Wait for tooltip to render
        nextTick(() => {
            updatePosition()
            calculateTooltipStyle()
        })
    }
})

// Update position on window resize
onMounted(() => {
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', handleScroll, true) // true for capture phase to handle all scrollable parents
})

onBeforeUnmount(() => {
    if (showTimeout.value) {
        clearTimeout(showTimeout.value)
    }
    window.removeEventListener('resize', updatePosition)
    window.removeEventListener('scroll', handleScroll, true)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("span", {
      ref_key: "trigger",
      ref: trigger,
      class: _normalizeClass(_ctx.$style.triggerElement),
      onMouseenter: showTooltip,
      onMouseleave: hideTooltip,
      onFocus: showTooltip,
      onBlur: hideTooltip
    }, [
      _renderSlot(_ctx.$slots, "default")
    ], 34),
    (isVisible.value)
      ? (_openBlock(), _createBlock(_Teleport, {
          key: 0,
          to: "body"
        }, [
          _createElementVNode("div", {
            ref_key: "tooltip",
            ref: tooltip,
            class: _normalizeClass([
                _ctx.$style.tooltip,
                _ctx.$style[`tooltip-${adjustedPosition.value}`],
                _ctx.$style[`tooltip-${adjustedPosition.value}-${props.theme}`],
                props.theme ? _ctx.$style[`tooltip-theme-${props.theme}`] : ``,
            ]),
            style: _normalizeStyle(tooltipStyle.value),
            innerHTML: _ctx.content
          }, null, 14, _hoisted_1)
        ]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})